// AdminNewsletter.jsx — Updated with:
// 1. Date range filter on subscribers
// 2. Draft templates tab (save, load, delete)
// 3. Resend button on failed campaigns
// 4. (Donate button fix is in App.js — wrap with {isPublicUser && <DonateButton />})

import React, { useState, useEffect, useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { Sidebar } from './Sidebar';
import RichTextEditor from '../ReusableComponents/RichTextEditor';
import api from '../utils/axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AC = { withCredentials: true };

const css = `
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
  @keyframes shimmer { 0%,100%{opacity:1} 50%{opacity:.5} }
  .nl-fade { animation:fadeIn .2s ease; }
  .nl-sk { background:linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 50%,#f3f4f6 75%); background-size:200% 100%; animation:shimmer 1.2s infinite; border-radius:4px; }
  .nl-btn { display:inline-flex;align-items:center;gap:6px;padding:9px 16px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;border:none;transition:opacity .15s; }
  .nl-btn:hover{opacity:.85} .nl-btn:disabled{opacity:.5;cursor:not-allowed}
  .nl-tab { padding:9px 16px;border:none;background:none;font-size:13.5px;font-weight:500;cursor:pointer;color:#6b7280;border-bottom:2px solid transparent;transition:all .15s;display:inline-flex;align-items:center;gap:6px; }
  .nl-tab.active { color:#111827;border-bottom-color:#111827;font-weight:700; }
  .nl-input { width:100%;padding:9px 12px;font-size:13.5px;border:1px solid #e5e7eb;border-radius:8px;outline:none;box-sizing:border-box; }
  .nl-input:focus { border-color:#6b7280; }
  .nl-checkbox { width:16px;height:16px;cursor:pointer;accent-color:#111827; }
  .nl-th { padding:11px 14px;color:#fff;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.6px;text-align:left; }
  .nl-td { padding:13px 14px;border-bottom:1px solid #f3f4f6;font-size:13.5px;color:#374151;vertical-align:middle; }
  tr:last-child .nl-td { border-bottom:none; }
`;

// ── Icons ─────────────────────────────────────────────────────────────────────
const Ic = {
  Send:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  Delete:  () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  Search:  () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  Refresh: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.99"/></svg>,
  Mail:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  Users:   () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Draft:   () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  History: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Load:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
  Resend:  () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.99"/></svg>,
  Save:    () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
};

// ── Small reusable components ─────────────────────────────────────────────────
const Badge = ({ label, bg, color }) => (
  <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:bg, color }}>{label}</span>
);

const ConfirmModal = ({ title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) => (
  <div onClick={onCancel} style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
    <div onClick={e => e.stopPropagation()} style={{ background:'#fff',borderRadius:16,padding:32,maxWidth:380,width:'100%',boxShadow:'0 25px 60px rgba(0,0,0,0.25)',textAlign:'center' }}>
      <h3 style={{ fontSize:17,fontWeight:700,color:'#111827',margin:'0 0 10px' }}>{title}</h3>
      <p style={{ color:'#6b7280',fontSize:14,margin:'0 0 24px',lineHeight:1.6 }}>{message}</p>
      <div style={{ display:'flex',gap:10 }}>
        <button onClick={onCancel} style={{ flex:1,padding:'10px 0',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',color:'#374151',fontWeight:600,fontSize:14,cursor:'pointer' }}>Cancel</button>
        <button onClick={onConfirm} style={{ flex:1,padding:'10px 0',borderRadius:8,border:'none',background:danger?'#ef4444':'#111827',color:'#fff',fontWeight:600,fontSize:14,cursor:'pointer' }}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

const SkeletonRows = ({ cols, rows = 5 }) => (
  Array.from({ length: rows }).map((_, i) => (
    <tr key={i}>{Array.from({ length: cols }).map((_, j) => <td key={j} className="nl-td"><div className="nl-sk" style={{ height:13 }} /></td>)}</tr>
  ))
);

const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'Asia/Karachi' }) : '—';

// ─────────────────────────────────────────────────────────────────────────────
const AdminNewsletter = () => {
  const alert    = useAlert();
  const { user } = useSelector(s => s.user);

  const [activeTab, setActiveTab]   = useState('subscribers');

  // ── Subscribers ───────────────────────────────────────────────────────────
  const [subscribers, setSubscribers]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [stats, setStats]               = useState({ total_active:0, total_unsubscribed:0 });
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [page, setPage]                 = useState(1);
  const [pagination, setPagination]     = useState({});
  const [selected, setSelected]         = useState([]);
  const [deleteModal, setDeleteModal]   = useState(false);

  // ── Compose ───────────────────────────────────────────────────────────────
  const [subject, setSubject]     = useState('');
  const [bodyHtml, setBodyHtml]   = useState('');
  const [sendTo, setSendTo]       = useState('all');
  const [sending, setSending]     = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // ── Drafts ────────────────────────────────────────────────────────────────
  const [drafts, setDrafts]           = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftName, setDraftName]     = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [deleteDraftTarget, setDeleteDraftTarget] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null); // draft being edited

  // ── Logs ──────────────────────────────────────────────────────────────────
  const [logs, setLogs]             = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resending, setResending]   = useState(false);

  // ── Fetch subscribers ─────────────────────────────────────────────────────
  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/api/v1/admin/newsletter/subscribers`, {
        params: { status:statusFilter, page, limit:50, search, date_from:dateFrom, date_to:dateTo },
        ...AC,
      });
      setSubscribers(res.data.data || []);
      setPagination(res.data.pagination || {});
      setStats(res.data.stats || {});
      setSelected([]);
    } catch { alert.error('Failed to load subscribers.'); }
    finally { setLoading(false); }
  }, [statusFilter, page, search, dateFrom, dateTo]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  // ── Fetch drafts ──────────────────────────────────────────────────────────
  const fetchDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/api/v1/admin/newsletter/drafts`, AC);
      setDrafts(res.data.data || []);
    } catch { alert.error('Failed to load drafts.'); }
    finally { setDraftsLoading(false); }
  }, []);

  // ── Fetch logs ────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await api.get(`${API_BASE_URL}/api/v1/admin/newsletter/logs`, AC);
      setLogs(res.data.data || []);
    } catch { alert.error('Failed to load logs.'); }
    finally { setLogsLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'drafts') fetchDrafts(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'logs')   fetchLogs();   }, [activeTab]);

  // ── Select helpers ────────────────────────────────────────────────────────
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll    = ()   => setSelected(selected.length === subscribers.length ? [] : subscribers.map(s => s.subscriber_id));

  // ── Delete subscribers ────────────────────────────────────────────────────
  const handleDeleteSubscribers = async () => {
    try {
      await Promise.all(selected.map(id => api.delete(`${API_BASE_URL}/api/v1/admin/newsletter/subscribers/${id}`, AC)));
      alert.success(`${selected.length} deleted.`);
      setDeleteModal(false);
      fetchSubscribers();
    } catch { alert.error('Failed to delete.'); }
  };

  // ── Send email ────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!subject.trim()) { alert.error('Subject is required.'); return; }
    if (!bodyHtml.trim() || bodyHtml === '<p><br></p>') { alert.error('Email body is required.'); return; }
    if (sendTo === 'selected' && selected.length === 0) { alert.error('Select subscribers first from the Subscribers tab.'); return; }

    setSending(true); setSendResult(null);
    try {
      const res = await api.post(`${API_BASE_URL}/api/v1/admin/newsletter/send`, {
        subject, body_html: bodyHtml,
        send_to: sendTo === 'all' ? 'all' : selected,
      }, AC);
      setSendResult({ type:'success', message: res.data.message });
      alert.success(`Queued for ${res.data.recipients} subscribers.`);
      setSubject(''); setBodyHtml(''); setDraftName(''); setEditingDraft(null);
    } catch (err) {
      setSendResult({ type:'error', message: err.response?.data?.message || 'Send failed.' });
      alert.error('Failed to send.');
    } finally { setSending(false); }
  };

  // ── Draft: save / update ──────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!draftName.trim()) { alert.error('Please enter a template name.'); return; }
    if (!subject.trim() || !bodyHtml.trim()) { alert.error('Subject and body are required to save a draft.'); return; }

    setSavingDraft(true);
    try {
      if (editingDraft) {
        await api.put(`${API_BASE_URL}/api/v1/admin/newsletter/drafts/${editingDraft.draft_id}`, { name:draftName, subject, body_html:bodyHtml }, AC);
        alert.success('Draft updated.');
      } else {
        await api.post(`${API_BASE_URL}/api/v1/admin/newsletter/drafts`, { name:draftName, subject, body_html:bodyHtml }, AC);
        alert.success('Draft saved.');
      }
      setEditingDraft(null); setDraftName('');
      // Refresh drafts if on that tab
      if (activeTab === 'drafts') fetchDrafts();
    } catch { alert.error('Failed to save draft.'); }
    finally { setSavingDraft(false); }
  };

  // ── Draft: load into compose ──────────────────────────────────────────────
  const loadDraft = (draft) => {
    setSubject(draft.subject);
    setBodyHtml(draft.body_html);
    setDraftName(draft.name);
    setEditingDraft(draft);
    setActiveTab('compose');
    setSendResult(null);
    alert.success(`Template "${draft.name}" loaded into compose.`);
  };

  // ── Draft: delete ─────────────────────────────────────────────────────────
  const handleDeleteDraft = async () => {
    try {
      await api.delete(`${API_BASE_URL}/api/v1/admin/newsletter/drafts/${deleteDraftTarget.draft_id}`, AC);
      alert.success('Draft deleted.');
      setDeleteDraftTarget(null);
      fetchDrafts();
    } catch { alert.error('Failed to delete draft.'); }
  };

  // ── Resend failed campaign ────────────────────────────────────────────────
  const handleResend = async () => {
    setResending(true);
    try {
      const res = await api.post(`${API_BASE_URL}/api/v1/admin/newsletter/resend/${resendTarget.log_id}`, {}, AC);
      alert.success(res.data.message);
      setResendTarget(null);
      fetchLogs();
    } catch (err) {
      alert.error(err.response?.data?.message || 'Resend failed.');
    } finally { setResending(false); }
  };

  // ── Clear date filter ─────────────────────────────────────────────────────
  const clearDateFilter = () => { setDateFrom(''); setDateTo(''); setPage(1); };

  return (
    <>
      <style>{css}</style>
      <div style={{ display:'flex', background:'#f8fafc', minHeight:'100vh' }}>
        <Sidebar />
        <div style={{ flex:1, padding:'32px 28px', overflowX:'auto' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{ marginBottom:24 }}>
            <h1 style={{ fontSize:21, fontWeight:800, color:'#111827', margin:'0 0 4px' }}>Newsletter</h1>
            <p style={{ fontSize:13, color:'#6b7280', margin:0 }}>Manage subscribers, templates and bulk emails</p>
          </div>

          {/* ── Stat cards ─────────────────────────────────────────── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:14, marginBottom:24 }}>
            {[
              { label:'Active',     value: stats.total_active       || 0, color:'#16a34a' },
              { label:'Unsubscribed', value: stats.total_unsubscribed || 0, color:'#9ca3af' },
              { label:'Selected',   value: selected.length,               color:'#2563eb' },
              { label:'Drafts',     value: drafts.length,                 color:'#7c3aed' },
            ].map(s => (
              <div key={s.label} style={{ background:'#fff', borderRadius:12, padding:'14px 18px', border:'1px solid #f0f0f0' }}>
                <p style={{ margin:'0 0 4px', fontSize:11, color:'#6b7280', fontWeight:700, textTransform:'uppercase', letterSpacing:.5 }}>{s.label}</p>
                <p style={{ margin:0, fontSize:26, fontWeight:800, color:s.color }}>{s.value.toLocaleString()}</p>
              </div>
            ))}
          </div>

          {/* ── Tabs ─────────────────────────────────────────────────── */}
          <div style={{ display:'flex', gap:0, borderBottom:'1px solid #e5e7eb', marginBottom:20 }}>
            {[
              { id:'subscribers', label:'Subscribers',    icon:<Ic.Users /> },
              { id:'compose',     label:'Compose',        icon:<Ic.Mail /> },
              { id:'drafts',      label:'Templates',      icon:<Ic.Draft /> },
              { id:'logs',        label:'Send History',   icon:<Ic.History /> },
            ].map(t => (
              <button key={t.id} className={`nl-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* ════════════════════════════════════════════════════════════
              TAB: SUBSCRIBERS
          ════════════════════════════════════════════════════════════ */}
          {activeTab === 'subscribers' && (
            <div className="nl-fade">
              {/* Toolbar */}
              <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'flex-end' }}>
                {/* Search */}
                <div style={{ position:'relative', minWidth:200, flex:1, maxWidth:280 }}>
                  <div style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }}><Ic.Search /></div>
                  <input className="nl-input" style={{ paddingLeft:30 }} placeholder="Search email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>

                {/* Status */}
                <select className="nl-input" style={{ width:'auto', minWidth:140 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="active">Active</option>
                  <option value="unsubscribed">Unsubscribed</option>
                  <option value="">All</option>
                </select>

                {/* ── DATE FILTER ── */}
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#6b7280', whiteSpace:'nowrap' }}>From</label>
                  <input type="date" className="nl-input" style={{ width:150 }} value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:'#6b7280', whiteSpace:'nowrap' }}>To</label>
                  <input type="date" className="nl-input" style={{ width:150 }} value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }} />
                </div>
                {(dateFrom || dateTo) && (
                  <button className="nl-btn" style={{ background:'#fef2f2', color:'#ef4444', border:'1px solid #fecaca' }} onClick={clearDateFilter}>
                    Clear dates
                  </button>
                )}

                <button className="nl-btn" style={{ background:'#fff', border:'1px solid #e5e7eb', color:'#374151' }} onClick={fetchSubscribers}>
                  <Ic.Refresh /> Refresh
                </button>
                {selected.length > 0 && <>
                  <button className="nl-btn" style={{ background:'#ef4444', color:'#fff' }} onClick={() => setDeleteModal(true)}>
                    <Ic.Delete /> Delete ({selected.length})
                  </button>
                  <button className="nl-btn" style={{ background:'#111827', color:'#fff' }} onClick={() => { setSendTo('selected'); setActiveTab('compose'); }}>
                    <Ic.Mail /> Email ({selected.length})
                  </button>
                </>}
              </div>

              {/* Table */}
              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#1f2937' }}>
                      <th className="nl-th" style={{ width:40 }}><input type="checkbox" className="nl-checkbox" checked={selected.length === subscribers.length && subscribers.length > 0} onChange={toggleAll} /></th>
                      <th className="nl-th">Sr.No.</th>
                      <th className="nl-th">Email</th>
                      <th className="nl-th">Status</th>
                      <th className="nl-th">Source</th>
                      <th className="nl-th">Subscribed On</th>
                      <th className="nl-th" style={{ textAlign:'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <SkeletonRows cols={6} /> : subscribers.length > 0 ? subscribers.map(sub => (
                      <tr key={sub.subscriber_id} onMouseEnter={e => e.currentTarget.style.background='#f9fafb'} onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td className="nl-td"><input type="checkbox" className="nl-checkbox" checked={selected.includes(sub.subscriber_id)} onChange={() => toggleSelect(sub.subscriber_id)} /></td>
                        <td className="nl-td" style={{ fontWeight:500, color:'#111827' }}>{sub.sr_no}</td>
                        <td className="nl-td" style={{ fontWeight:500, color:'#111827' }}>{sub.email}</td>
                        <td className="nl-td"><Badge label={sub.status} bg={sub.status==='active'?'#dcfce7':'#f3f4f6'} color={sub.status==='active'?'#16a34a':'#6b7280'} /></td>
                        <td className="nl-td" style={{ color:'#6b7280', fontSize:12 }}>{sub.source || 'footer'}</td>
                        <td className="nl-td" style={{ color:'#6b7280', fontSize:12 }}>{formatDate(sub.subscribed_at)}</td>
                        <td className="nl-td" style={{ textAlign:'center' }}>
                          <button onClick={() => { setSelected([sub.subscriber_id]); setDeleteModal(true); }}
                            style={{ background:'none',border:'none',cursor:'pointer',color:'#ef4444',padding:'4px 8px',borderRadius:6,display:'inline-flex',alignItems:'center',gap:4,fontSize:12,fontWeight:500 }}
                            onMouseEnter={e => e.currentTarget.style.background='#fef2f2'} onMouseLeave={e => e.currentTarget.style.background='none'}>
                            <Ic.Delete /> Delete
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} style={{ padding:'52px 20px', textAlign:'center', color:'#9ca3af', fontSize:14 }}>
                        {dateFrom || dateTo ? 'No subscribers found for this date range.' : 'No subscribers found.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
                  {Array.from({ length: pagination.totalPages }, (_, i) => i+1).map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{ width:34,height:34,borderRadius:8,border:'1px solid',fontWeight:600,fontSize:13,cursor:'pointer',background:p===page?'#111827':'#fff',color:p===page?'#fff':'#374151',borderColor:p===page?'#111827':'#e5e7eb' }}>{p}</button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB: COMPOSE
          ════════════════════════════════════════════════════════════ */}
          {activeTab === 'compose' && (
            <div className="nl-fade" style={{ maxWidth:800 }}>
              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden' }}>
                <div style={{ padding:'14px 20px', borderBottom:'1px solid #f3f4f6', background:'#f9fafb', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#111827' }}>
                    Compose Email {editingDraft && <span style={{ fontSize:12, color:'#7c3aed', fontWeight:500 }}>— editing "{editingDraft.name}"</span>}
                  </p>
                  {editingDraft && (
                    <button onClick={() => { setEditingDraft(null); setDraftName(''); }} style={{ background:'none', border:'none', fontSize:12, color:'#6b7280', cursor:'pointer', textDecoration:'underline' }}>
                      Clear template
                    </button>
                  )}
                </div>
                <div style={{ padding:24, display:'flex', flexDirection:'column', gap:18 }}>

                  {/* Send to */}
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:8, textTransform:'uppercase', letterSpacing:.5 }}>Send To</label>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                      {[{ val:'all', label:`All active (${stats.total_active})` }, { val:'selected', label:`Selected only (${selected.length})` }].map(o => (
                        <label key={o.val} style={{ display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,border:`1px solid ${sendTo===o.val?'#111827':'#e5e7eb'}`,cursor:'pointer',fontSize:13,fontWeight:500,background:sendTo===o.val?'#f9fafb':'#fff' }}>
                          <input type="radio" name="sendTo" value={o.val} checked={sendTo===o.val} onChange={() => setSendTo(o.val)} />
                          {o.label}
                        </label>
                      ))}
                    </div>
                    {sendTo==='selected' && selected.length===0 && <p style={{ fontSize:12,color:'#f59e0b',margin:'6px 0 0' }}>⚠ No subscribers selected. Go to Subscribers tab first.</p>}
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:.5 }}>Subject</label>
                    <input className="nl-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Ramadan Campaign Update" />
                  </div>

                  {/* Body */}
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:.5 }}>Email Body</label>
                    <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write your email content here…" height={340} />
                    <p style={{ fontSize:11, color:'#9ca3af', margin:'6px 0 0' }}>An unsubscribe link is added automatically at the bottom of every email.</p>
                  </div>

                  {/* Save as draft */}
                  <div style={{ background:'#f9fafb', borderRadius:10, padding:14, border:'1px solid #e5e7eb' }}>
                    <p style={{ fontSize:12, fontWeight:700, color:'#374151', margin:'0 0 10px', textTransform:'uppercase', letterSpacing:.5 }}>
                      {editingDraft ? 'Update Template' : 'Save as Template'}
                    </p>
                    <div style={{ display:'flex', gap:10 }}>
                      <input className="nl-input" value={draftName} onChange={e => setDraftName(e.target.value)} placeholder="Template name e.g. Eid Appeal 2026" style={{ flex:1 }} />
                      <button className="nl-btn" style={{ background:'#7c3aed', color:'#fff', whiteSpace:'nowrap' }} onClick={handleSaveDraft} disabled={savingDraft}>
                        <Ic.Save /> {savingDraft ? 'Saving…' : editingDraft ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </div>

                  {/* Result */}
                  {sendResult && (
                    <div style={{ padding:'12px 16px', borderRadius:8, background:sendResult.type==='success'?'#f0fdf4':'#fef2f2', border:`1px solid ${sendResult.type==='success'?'#bbf7d0':'#fecaca'}`, fontSize:13, color:sendResult.type==='success'?'#16a34a':'#ef4444' }}>
                      {sendResult.message}
                    </div>
                  )}

                  {/* Send */}
                  <button className="nl-btn" onClick={handleSend} disabled={sending} style={{ background:'#111827',color:'#fff',alignSelf:'flex-start',padding:'11px 24px',fontSize:14 }}>
                    <Ic.Send /> {sending ? 'Sending…' : sendTo==='all' ? `Send to all (${stats.total_active})` : `Send to ${selected.length} selected`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB: DRAFT TEMPLATES
          ════════════════════════════════════════════════════════════ */}
          {activeTab === 'drafts' && (
            <div className="nl-fade">
              {draftsLoading ? (
                <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}><tbody><SkeletonRows cols={4} rows={4} /></tbody></table>
                </div>
              ) : drafts.length > 0 ? (
                <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:'#1f2937' }}>
                        <th className="nl-th">Template Name</th>
                        <th className="nl-th">Subject</th>
                        <th className="nl-th">Last Updated</th>
                        <th className="nl-th" style={{ textAlign:'center', width:200 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drafts.map(draft => (
                        <tr key={draft.draft_id} onMouseEnter={e => e.currentTarget.style.background='#f9fafb'} onMouseLeave={e => e.currentTarget.style.background=''}>
                          <td className="nl-td" style={{ fontWeight:600, color:'#111827' }}>{draft.name}</td>
                          <td className="nl-td" style={{ color:'#6b7280', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{draft.subject}</td>
                          <td className="nl-td" style={{ color:'#9ca3af', fontSize:12 }}>{formatDate(draft.updated_at)}</td>
                          <td className="nl-td" style={{ textAlign:'center' }}>
                            <div style={{ display:'flex', justifyContent:'center', gap:6 }}>
                              <button onClick={() => loadDraft(draft)} className="nl-btn" style={{ background:'#eff6ff',color:'#2563eb',padding:'6px 12px',fontSize:12 }}>
                                <Ic.Load /> Use
                              </button>
                              <button onClick={() => setDeleteDraftTarget(draft)} className="nl-btn" style={{ background:'#fef2f2',color:'#ef4444',padding:'6px 12px',fontSize:12 }}>
                                <Ic.Delete /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', padding:'52px 20px', textAlign:'center' }}>
                  <p style={{ fontSize:15, fontWeight:700, color:'#374151', margin:'0 0 6px' }}>No templates saved yet</p>
                  <p style={{ fontSize:13, color:'#9ca3af', margin:'0 0 20px' }}>Go to Compose, write an email, and save it as a template for future use.</p>
                  <button className="nl-btn" style={{ background:'#111827', color:'#fff', margin:'0 auto' }} onClick={() => setActiveTab('compose')}>
                    <Ic.Mail /> Go to Compose
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              TAB: SEND HISTORY
          ════════════════════════════════════════════════════════════ */}
          {activeTab === 'logs' && (
            <div className="nl-fade">
              <div style={{ background:'#fff', borderRadius:12, border:'1px solid #f0f0f0', overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'#1f2937' }}>
                      <th className="nl-th">Subject</th>
                      <th className="nl-th">Recipients</th>
                      <th className="nl-th" style={{ color:'#86efac' }}>Sent ✓</th>
                      <th className="nl-th" style={{ color:'#fca5a5' }}>Failed ✗</th>
                      <th className="nl-th">Status</th>
                      <th className="nl-th">Date</th>
                      <th className="nl-th" style={{ textAlign:'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading ? <SkeletonRows cols={7} /> : logs.length > 0 ? logs.map(log => (
                      <tr key={log.log_id} onMouseEnter={e => e.currentTarget.style.background='#f9fafb'} onMouseLeave={e => e.currentTarget.style.background=''}>
                        <td className="nl-td" style={{ fontWeight:500, color:'#111827', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.subject}</td>
                        <td className="nl-td">{log.recipient_count}</td>
                        <td className="nl-td" style={{ color:'#16a34a', fontWeight:600 }}>{log.success_count}</td>
                        <td className="nl-td" style={{ color:log.failed_count>0?'#ef4444':'#6b7280', fontWeight:log.failed_count>0?700:400 }}>{log.failed_count}</td>
                        <td className="nl-td">
                          <Badge
                            label={log.status}
                            bg={log.status==='completed'?'#dcfce7':log.status==='sending'?'#fef9c3':'#fef2f2'}
                            color={log.status==='completed'?'#16a34a':log.status==='sending'?'#ca8a04':'#ef4444'}
                          />
                        </td>
                        <td className="nl-td" style={{ color:'#6b7280', fontSize:12 }}>{formatDate(log.sent_at)}</td>
                        <td className="nl-td" style={{ textAlign:'center' }}>
                          {/* ── RESEND button — only shown for failed campaigns ── */}
                          {log.status === 'failed' && (
                            <button onClick={() => setResendTarget(log)} className="nl-btn"
                              style={{ background:'#fef9c3',color:'#92400e',padding:'6px 12px',fontSize:12,border:'1px solid #fde68a' }}>
                              <Ic.Resend /> Resend
                            </button>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={7} style={{ padding:'52px 20px', textAlign:'center', color:'#9ca3af', fontSize:14 }}>No emails sent yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────── */}
      {deleteModal && (
        <ConfirmModal
          title={`Delete ${selected.length} subscriber${selected.length>1?'s':''}?`}
          message="This cannot be undone."
          confirmLabel="Delete"
          danger
          onConfirm={handleDeleteSubscribers}
          onCancel={() => setDeleteModal(false)}
        />
      )}
      {deleteDraftTarget && (
        <ConfirmModal
          title={`Delete "${deleteDraftTarget.name}"?`}
          message="This template will be permanently removed."
          confirmLabel="Delete"
          danger
          onConfirm={handleDeleteDraft}
          onCancel={() => setDeleteDraftTarget(null)}
        />
      )}
      {resendTarget && (
        <ConfirmModal
          title="Resend this campaign?"
          message={`"${resendTarget.subject}" will be resent to all active subscribers. A new log entry will be created.`}
          confirmLabel={resending ? 'Sending…' : 'Yes, Resend'}
          onConfirm={handleResend}
          onCancel={() => setResendTarget(null)}
        />
      )}
    </>
  );
};

export default AdminNewsletter;